/**
 * The plan as one bar, with every template budget laid end to end along it.
 *
 * The track is scaled to whichever is larger, the plan fee or the total
 * budgeted, so going over plan reads as budgets running *past* the plan
 * marker, with the part the plan does not pay for shaded amber.
 *
 * Used twice — the pool at the top of the tab, and the live preview in the
 * editor, where the template being edited is lit and the rest step back.
 */
import { useEffect, useState } from 'react'
import { usePrefersReducedMotion } from './BillingPrimitives'

export default function BudgetAllocationBar({
  segments = [],
  fee,
  format,
  highlightId = null,
  height = 'h-4',
  showCaption = true,
  label = 'Template budgets against the plan',
}) {
  const reduced = usePrefersReducedMotion()
  const [mounted, setMounted] = useState(reduced)
  const [hovered, setHovered] = useState(null)

  useEffect(() => {
    if (reduced) return
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [reduced])

  const total = segments.reduce((sum, s) => sum + Math.max(0, s.amount), 0)
  const scaleMax = Math.max(fee || 0, total, 1)
  const pct = v => (mounted ? (v / scaleMax) * 100 : 0)
  const feePct = ((fee || 0) / scaleMax) * 100
  const over = total > fee

  let cursor = 0
  const placed = segments
    .filter(s => s.amount > 0)
    .map(s => {
      const left = cursor
      cursor += s.amount
      return { ...s, left }
    })

  const focus = placed.find(s => s.id === (hovered ?? highlightId))

  return (
    <div>
      <div className="relative pt-5">
        {/* plan marker label, above the track at the fee position */}
        <div
          className="absolute top-0 -translate-x-full text-[10px] font-semibold uppercase tracking-wider
                     text-gray-500 dark:text-gray-400 whitespace-nowrap transition-[left] duration-700 ease-out pr-1"
          style={{ left: `${Math.max(feePct, 18)}%` }}
        >
          Plan · {format(fee)}
        </div>

        <div
          className={`relative ${height} rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden
                      ring-1 ring-inset ring-gray-200/70 dark:ring-gray-700/60`}
          role="img"
          aria-label={`${label}: ${format(total)} budgeted against a ${format(fee)} plan${over ? `, ${format(total - fee)} over` : ''}`}
        >
          {placed.map(s => {
            const dim = (hovered ?? highlightId) && s.id !== (hovered ?? highlightId)
            return (
              <div
                key={s.id}
                onMouseEnter={() => setHovered(s.id)}
                onMouseLeave={() => setHovered(null)}
                className="absolute inset-y-0 border-r border-white/70 dark:border-gray-900/70 last:border-r-0
                           transition-[left,width,opacity] duration-700 ease-out"
                style={{
                  left: `${pct(s.left)}%`,
                  width: `${Math.max(pct(s.amount), mounted ? 0.6 : 0)}%`,
                  backgroundColor: s.color,
                  opacity: dim ? 0.35 : 1,
                }}
              />
            )
          })}

          {/* what the plan does not pay for */}
          {over && (
            <div
              className="absolute inset-y-0 bg-amber-400/90 dark:bg-amber-500/80
                         transition-[left,width] duration-700 ease-out pointer-events-none"
              style={{ left: `${mounted ? feePct : 0}%`, width: `${mounted ? 100 - feePct : 0}%` }}
            />
          )}
        </div>

        {/* the plan itself */}
        <div
          className={`absolute bottom-[-3px] w-0.5 rounded-full bg-gray-900 dark:bg-gray-100 transition-[left] duration-700 ease-out
                      ${height === 'h-4' ? 'top-[16px]' : 'top-[14px]'}`}
          style={{ left: `calc(${feePct}% - 1px)` }}
          aria-hidden="true"
        />
      </div>

      {showCaption && (
        <div className="mt-2.5 min-h-[18px] text-[11px] text-gray-500 dark:text-gray-400">
          {focus ? (
            <span key={focus.id} className="inline-flex items-center gap-1.5 animate-panel-in">
              <i className="w-2 h-2 rounded-sm" style={{ backgroundColor: focus.color }} />
              <span className="font-semibold text-gray-800 dark:text-gray-200">{focus.label}</span>
              <span className="tabular-nums">{format(focus.amount)}</span>
              <span className="text-gray-400 dark:text-gray-500 tabular-nums">
                · {fee > 0 ? Math.round((focus.amount / fee) * 100) : 0}% of plan
              </span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <i className="w-2 h-2 rounded-sm bg-blue-500 dark:bg-blue-400" /> Budgeted
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="w-2 h-2 rounded-sm bg-gray-200 dark:bg-gray-700" /> Unallocated
              </span>
              {over && (
                <span className="inline-flex items-center gap-1.5">
                  <i className="w-2 h-2 rounded-sm bg-amber-400 dark:bg-amber-500" /> Over plan
                </span>
              )}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
